// src/hooks/usePushNotifications.ts
// Manages PWA service worker registration and Web Push subscription lifecycle.
// Only meaningful for Admin / Reception roles.

import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '../api/client';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

interface PushState {
  supported: boolean;         // Browser supports push notifications
  swRegistered: boolean;      // Service worker is registered
  permission: PermissionState;
  subscribed: boolean;        // Currently subscribed to push
  loading: boolean;           // Busy (subscribing / unsubscribing)
  error: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);
  return arr.buffer as ArrayBuffer;
}

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await apiGet<{ publicKey: string }>('/api/push/vapid-public-key');
    return res.publicKey;
  } catch {
    return null;
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function usePushNotifications() {
  const [state, setState] = useState<PushState>({
    supported: false,
    swRegistered: false,
    permission: 'default',
    subscribed: false,
    loading: true,
    error: null,
  });

  const [swReg, setSwReg] = useState<ServiceWorkerRegistration | null>(null);

  // ── Register service worker + check current subscription state ─────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState((s) => ({ ...s, supported: false, loading: false }));
      return;
    }

    setState((s) => ({ ...s, supported: true, permission: Notification.permission as PermissionState }));

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then(async (reg) => {
        setSwReg(reg);
        const existing = await reg.pushManager.getSubscription();
        setState((s) => ({
          ...s,
          swRegistered: true,
          subscribed: !!existing,
          loading: false,
        }));

        // Listen for SW messages (e.g. navigate command from notification click)
        navigator.serviceWorker.addEventListener('message', (e) => {
          if (e.data?.type === 'NAVIGATE' && e.data.url) {
            window.location.href = e.data.url;
          }
        });
      })
      .catch((err) => {
        console.error('SW registration failed:', err);
        setState((s) => ({ ...s, swRegistered: false, loading: false, error: String(err) }));
      });
  }, []);

  // ── Subscribe ──────────────────────────────────────────────────────────────
  const subscribe = useCallback(async () => {
    if (!swReg) return;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      // 1. Request notification permission
      const permission = await Notification.requestPermission();
      setState((s) => ({ ...s, permission: permission as PermissionState }));
      if (permission !== 'granted') {
        setState((s) => ({ ...s, loading: false, error: 'Notification permission denied' }));
        return;
      }

      // 2. Fetch VAPID public key from server
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) {
        setState((s) => ({ ...s, loading: false, error: 'Push not configured on server yet' }));
        return;
      }

      // 3. Subscribe via PushManager
      const sub = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      const json = sub.toJSON();

      // 4. Send subscription to backend
      await apiPost('/api/push/subscribe', {
        endpoint: sub.endpoint,
        p256DH: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
        userAgent: navigator.userAgent.substring(0, 300),
      });

      setState((s) => ({ ...s, subscribed: true, loading: false }));
    } catch (err) {
      console.error('Push subscribe failed:', err);
      setState((s) => ({ ...s, loading: false, error: String(err) }));
    }
  }, [swReg]);

  // ── Unsubscribe ────────────────────────────────────────────────────────────
  const unsubscribe = useCallback(async () => {
    if (!swReg) return;
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const sub = await swReg.pushManager.getSubscription();
      if (sub) {
        // Tell backend first
        await apiDelete('/api/push/subscribe', sub.endpoint);
        await sub.unsubscribe();
      }
      setState((s) => ({ ...s, subscribed: false, loading: false }));
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
      setState((s) => ({ ...s, loading: false, error: String(err) }));
    }
  }, [swReg]);

  return { ...state, subscribe, unsubscribe };
}
